import { GeneralThemesCommonSettingsType, ThemeName } from '@ps-int/general-themes';
import { GeneralThemesSettingsDatasyncSchemaType } from '@ps-int/general-themes/settings/schema';

import { LOCAL_ONPREM_THEME_STORAGE_KEY } from '../../settings/persistence';

const DEFAULT_GENERAL_THEMES_SETTINGS: GeneralThemesCommonSettingsType = {
    isVideoEnabled: false,
    isNewYear2024ThemeEnabled: false,
};

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const normalizeGeneralThemesState = (
    value?: Partial<GeneralThemesSettingsDatasyncSchemaType> | null
): GeneralThemesSettingsDatasyncSchemaType => ({
    theme: value?.theme ?? null,
    settings: {
        isVideoEnabled: Boolean(value?.settings?.isVideoEnabled),
        isNewYear2024ThemeEnabled: Boolean(value?.settings?.isNewYear2024ThemeEnabled),
    },
});

export const readStoredOnPremGeneralThemes = (): GeneralThemesSettingsDatasyncSchemaType | null => {
    try {
        const rawValue = localStorage.getItem(LOCAL_ONPREM_THEME_STORAGE_KEY);

        if (!rawValue) {
            return null;
        }

        const parsedValue = JSON.parse(rawValue);

        return isObject(parsedValue) ? normalizeGeneralThemesState(parsedValue) : null;
    } catch {
        return null;
    }
};

type ThemeChangeCallback = (themeName: ThemeName) => void;
type VideoBgChangeCallback = (enabled: boolean) => void;

export class OnPremGeneralThemesSettingsService {
    private data: GeneralThemesSettingsDatasyncSchemaType;
    private readonly enableThemeCallbacks = new Set<ThemeChangeCallback>();
    private readonly switchVideoBgCallbacks = new Set<VideoBgChangeCallback>();

    constructor(initialData?: Partial<GeneralThemesSettingsDatasyncSchemaType> | null) {
        this.data = normalizeGeneralThemesState(initialData);
    }

    getThemeName(): ThemeName | null {
        return this.data.theme ?? null;
    }

    getCommonSettings(): GeneralThemesCommonSettingsType {
        return {
            ...DEFAULT_GENERAL_THEMES_SETTINGS,
            ...this.data.settings,
        };
    }

    isVideoBgEnabled(): boolean {
        return this.getCommonSettings().isVideoEnabled;
    }

    async enableTheme<K extends ThemeName>(theme: K, settings?: GeneralThemesCommonSettingsType): Promise<void> {
        this.data = {
            theme,
            settings: {
                ...this.getCommonSettings(),
                ...settings,
            },
        };
        this.persist();
        this.enableThemeCallbacks.forEach((callback) => callback(theme));
    }

    async disableTheme(): Promise<void> {
        this.data = {
            theme: null,
            settings: this.getCommonSettings(),
        };
        this.persist();
    }

    onEnableTheme(callback: ThemeChangeCallback): () => void {
        this.enableThemeCallbacks.add(callback);

        return () => {
            this.enableThemeCallbacks.delete(callback);
        };
    }

    async enableVideoBg(): Promise<void> {
        this.updateVideoBg(true);
    }

    async disableVideoBg(): Promise<void> {
        this.updateVideoBg(false);
    }

    onSwitchVideoBg(callback: VideoBgChangeCallback): () => void {
        this.switchVideoBgCallbacks.add(callback);

        return () => {
            this.switchVideoBgCallbacks.delete(callback);
        };
    }

    private updateVideoBg(enabled: boolean): void {
        this.data = {
            ...this.data,
            settings: {
                ...this.getCommonSettings(),
                isVideoEnabled: enabled,
            },
        };
        this.persist();
        this.switchVideoBgCallbacks.forEach((callback) => callback(enabled));
    }

    private persist(): void {
        localStorage.setItem(LOCAL_ONPREM_THEME_STORAGE_KEY, JSON.stringify(this.data));
    }
}
